import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { Award, Sparkles, Leaf, GraduationCap, ArrowRight } from "lucide-react";
import BeforeAfter from "./BeforeAfter";

const CHAPTERS = [
  {
    num: "೧",
    title: "Music, Culture & the Arts",
    icon: Sparkles,
    body: "Trained in Karnatik music and steeped in the classical arts from childhood, Smt. Revathi Kamath grew up inside the disciplines of raga, laya and craft. That grounding in tradition — patient, rigorous, respectful of teachers — became the tone of everything she later built.",
  },
  {
    num: "೨",
    title: "Enterprise & Livelihoods",
    icon: Award,
    body: "As an entrepreneur, she has built enterprises that place women, rural artisans and first-generation earners at the centre. Her conviction — dignified work first, everything else after — has shaped a career that treats livelihood as the foundation of every social gain.",
  },
  {
    num: "೩",
    title: "Environment & Community",
    icon: Leaf,
    body: "Alongside her cultural and business work she has driven long-running community and environmental initiatives across Karnataka — reforestation drives, water-body restoration, and public-space revival — always in partnership with local villages and gram panchayats, never above them.",
  },
  {
    num: "೪",
    title: "Rural Education — the Current Chapter",
    icon: GraduationCap,
    body: "Akshara Daana is her present focus. A full-time commitment to Karnataka's rural schools: bringing quality teachers, safe classrooms, hygiene, digital learning and dignity to children who have long been asked to make do with less.",
  },
];

const RECOGNITIONS = [
  { label: "Karnataka Kalashree", body: "State recognition for lifetime contribution to arts and culture." },
  { label: "Philanthropist", body: "Six years of full-time work in rural Karnataka schools." },
  { label: "Entrepreneur", body: "Enterprises built around women, artisans and first-generation earners." },
  { label: "Environmentalist", body: "Sustained community-led ecological work across the state." },
];

const RIPPLES = [
  { k: "One child educated", v: "uplifts an entire family." },
  { k: "One school strengthened", v: "empowers an entire village." },
  { k: "One nation investing in education", v: "builds a stronger future." },
];

export default function About() {
  const ref = useReveal();
  const ref2 = useReveal();
  const ref3 = useReveal();
  const ref4 = useReveal();

  return (
    <section id="about" data-testid="about-section" className="relative py-16 md:py-32 bg-parchment overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* 1 · About Akshara Daana — Education Initiative */}
        <div id="initiative" ref={ref} className="reveal">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 min-w-0 text-center md:text-left">
              <span className="section-chip">About Akshara Daana</span>
              <h2 className="mt-4 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Akshara Daana &mdash; <em>Education Initiative.</em>
              </h2>
              <p className="mt-3 text-teal font-display font-bold text-xs md:text-sm uppercase tracking-widest">
                The Vision of Smt. Revathi Kamath
              </p>

              <div className="mt-6 space-y-5 font-display text-navy/80 text-base md:text-lg leading-relaxed">
                <p>
                  Akshara Daana is a visionary initiative conceived by Smt. Revathi Kamath with the noble mission of
                  transforming rural education through the collective power of community participation and crowdfunding.
                  The initiative believes that when people come together with a shared purpose, even small contributions
                  can create a lasting impact on the lives of thousands of children.
                </p>
                <p>
                  Over the past six years, Smt. Revathi Kamath has been working tirelessly for underprivileged rural
                  schools across Karnataka. Her efforts have focused on providing quality educational infrastructure and
                  creating a better learning environment for children. The initiative has undertaken the construction of
                  toilets, repair of damaged walls, windows, doors and flooring, waterproofing of school buildings,
                  rectification of water leakages, improvement of drinking water facilities, and development of school
                  playgrounds.
                </p>
                <p>
                  Akshara Daana has also introduced modern educational facilities by providing children&rsquo;s play
                  equipment, digital classrooms, projectors, computers, and improved teaching resources. Better-quality
                  teacher training programmes have been organised to enhance classroom learning. A mobile science
                  laboratory has been introduced to reach schools in remote areas, enabling practical, hands-on learning
                  experiences. <strong>More than 200 laboratory kits and science learning materials</strong> have already
                  been distributed to schools.
                </p>
              </div>

              {/* Full Before & After gallery — big, interactive, inline */}
              <div className="mt-12 -mx-4 sm:-mx-6 lg:-mx-8">
                <BeforeAfter embedded />
              </div>

              <div className="mt-10 space-y-5 font-display text-navy/80 text-base md:text-lg leading-relaxed">
                <p>
                  Having worked extensively at the grassroots level, Smt. Revathi Kamath realised that the need is far
                  greater than what one individual or organisation can achieve alone. This inspired her to create Akshara
                  Patra, a people&rsquo;s movement that encourages every individual to participate in rebuilding rural
                  education &mdash; not only across Karnataka but eventually throughout India and beyond.
                </p>
                <p>
                  Her heartfelt appeal is simple: every person can contribute an affordable amount, and together these
                  small contributions can transform hundreds of government schools that are presently in a state of
                  neglect into safe, functional, and inspiring centres of learning.
                </p>
                <p>She firmly believes in the timeless principle:</p>
              </div>

              <blockquote className="quote my-6 relative pl-6 border-l-4 border-coral text-navy text-xl sm:text-2xl md:text-3xl leading-snug text-left">
                &ldquo;Every drop makes an ocean.&rdquo;
              </blockquote>

              <div className="space-y-5 font-display text-navy/80 text-base md:text-lg leading-relaxed">
                <p>
                  If each one of us becomes that single drop, together we can create an ocean of opportunities for
                  millions of children. By educating a child, we strengthen a family; by strengthening families, we build
                  a stronger nation.
                </p>
                <p>
                  Smt. Revathi Kamath has observed that many children of daily-wage labourers remain trapped in the same
                  cycle of poverty because they lack access to quality education. Labourers&rsquo; children often become
                  labourers, drivers&rsquo; children become drivers &mdash; not because of lack of ability, but because
                  they lack educational opportunities. <strong>Akshara Daana seeks to break this cycle</strong> and empower
                  every child with the gift of education.
                </p>
                <p>
                  Helping one child uplifts an entire family. Helping one school transforms an entire community.
                </p>
              </div>

              <div className="mt-10 bg-navy text-white p-6 sm:p-7 md:p-9 text-left">
                <div className="quote text-coral text-xl sm:text-2xl md:text-4xl leading-tight">
                  &ldquo;Vidyādhanam Sarva Dhanam Pradhānam.&rdquo;
                </div>
                <div className="mt-2 font-display text-white/80 text-base md:text-lg">
                  The gift of education is the greatest of all gifts.
                </div>
                <div className="mt-5 h-px bg-white/15" />
                <p className="mt-5 font-display text-white/85 text-base md:text-lg leading-relaxed">
                  Let us join hands to create educated, empowered and responsible citizens. Together, we can build a
                  brighter future for our children, our communities, and our nation.
                </p>
                <Link
                  to="/get-involved#donate"
                  data-testid="initiative-donate-cta"
                  className="mt-6 inline-flex items-center gap-2 bg-teal hover:bg-teal-dark transition-colors text-white font-display font-bold text-sm px-6 py-3 rounded-sm"
                >
                  Become a Visionary
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Sidebar — ripples of impact */}
            <aside className="lg:col-span-4 lg:sticky lg:top-40 self-start space-y-6 min-w-0">
              <div className="manuscript-card manuscript-card-dark">
                <div className="text-teal font-display font-bold text-xs uppercase tracking-[0.24em]">
                  Every step ripples outward
                </div>
                <ul className="mt-5 space-y-5">
                  {RIPPLES.map((x) => (
                    <li key={x.k} className="border-l-2 border-teal pl-4">
                      <div className="font-display font-extrabold text-white text-lg">{x.k}</div>
                      <div className="font-display text-white/70 text-sm">{x.v}</div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-teal text-white p-7">
                <h5 className="font-display font-extrabold text-2xl leading-tight">
                  Join the Movement.
                </h5>
                <p className="mt-3 font-display text-white/90 text-sm leading-relaxed">
                  Akshara Daana invites every citizen, family, organisation and well-wisher to participate. No
                  contribution is too small.
                </p>
                <Link
                  to="/get-involved#donate"
                  data-testid="vision-donate-cta"
                  className="mt-5 inline-flex items-center gap-2 bg-white text-teal font-display font-bold text-sm px-5 py-3 rounded-sm hover:bg-cream transition-colors"
                >
                  Contribute
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="border-t border-navy/10 pt-6">
                <div className="stat-num text-5xl text-teal leading-none">200+</div>
                <div className="mt-2 font-display font-bold text-navy text-sm uppercase tracking-widest">Lab kits distributed</div>
                <p className="mt-2 font-display text-navy/70 text-sm">
                  Enabling hands-on science education for rural school children across Karnataka.
                </p>
              </div>
            </aside>
          </div>
        </div>

        {/* 2 · About the Founder — Smt. Revathi Kamath */}
        <div id="founder" ref={ref2} className="reveal mt-24 md:mt-32">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="manuscript-card">
                <div className="relative aspect-[4/5] overflow-hidden mb-5 bg-navy/10">
                  <img
                    src="/images/revathi-kamath.jpg"
                    alt="Smt. Revathi Kamath — founder of Akshara Daana"
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-sunflower text-navy text-[10px] uppercase font-display font-bold tracking-widest rounded-sm">
                    <Award className="w-3.5 h-3.5" /> Karnataka Kalashree Awardee
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-navy text-2xl md:text-3xl">Smt. Revathi Kamath</h3>
                  <p className="mt-1 text-sm text-navy/70 font-display">Founder · Akshara Daana</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-coral font-display font-bold">
                    Philanthropist · Entrepreneur · Environmentalist
                  </p>
                </div>
                <blockquote className="quote mt-6 text-navy text-xl leading-snug text-center border-t border-b border-dashed border-teal/40 py-5 px-2">
                  &ldquo;Education is the most sustainable form of social transformation.&rdquo;
                </blockquote>
              </div>
            </div>

            <div className="lg:col-span-7 min-w-0 text-center md:text-left">
              <span className="section-chip">The Founder</span>
              <h3 className="mt-3 font-display font-extrabold text-navy text-3xl md:text-5xl leading-[1.08] tracking-tight">
                Smt. Revathi Kamath, <em>Founder &mdash; Akshara Daana.</em>
              </h3>
              <div className="mt-5 space-y-4 font-display text-navy/80 text-base md:text-lg leading-relaxed">
                <p>
                  Smt. Revathi Kamath is a Karnataka Kalashree awardee, honoured by the state for a lifetime of
                  contribution to the arts. She is also an entrepreneur, environmentalist and philanthropist whose work
                  over three decades has moved fluently between the classical arts, dignified livelihoods, community-led
                  ecology and, most recently, rural education.
                </p>
                <p>
                  For the past six years she has committed her time, resources and personal presence to rural schools
                  across Karnataka — walking classrooms, sitting with teachers, meeting parents, and building small,
                  measurable improvements at each village school she adopts. Her belief is that transformation is slow,
                  granular and school-by-school — not a headline event.
                </p>
                <p>
                  Akshara Daana is her current, focused chapter. It carries forward everything she has learned
                  across music, business, community work and public service: that lasting change is made by people who
                  show up patiently, over years, and treat the villages they work with as equal partners.
                </p>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
                {RECOGNITIONS.map((r) => (
                  <div
                    key={r.label}
                    data-testid={`founder-tag-${r.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="border-l-4 border-teal pl-4 py-1"
                  >
                    <div className="font-display font-extrabold text-navy text-lg">{r.label}</div>
                    <div className="text-sm font-display text-navy/70">{r.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Founder journey — four chapters */}
        <div id="journey" ref={ref3} className="reveal mt-16 text-center md:text-left">
          <span className="section-chip">Her Journey · Four Chapters</span>
          <h3 className="mt-3 font-display font-extrabold text-navy text-3xl md:text-4xl leading-[1.1] tracking-tight max-w-3xl mx-auto md:mx-0">
            A life told in four <em>unhurried chapters.</em>
          </h3>

          <div className="mt-10 grid md:grid-cols-2 gap-6 text-left">
            {CHAPTERS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.num}
                  data-testid={`chapter-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="manuscript-card group hover:-translate-y-1 transition-transform"
                >
                  <div className="flex items-start gap-4 sm:gap-5">
                    <div className="text-center shrink-0">
                      <div className="text-sunflower leading-none font-display font-black text-[2.4rem] sm:text-[3.2rem]">
                        {c.num}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-navy/60 font-display font-bold mt-1">Chapter</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-4 h-4 text-teal" />
                        <h4 className="font-display font-extrabold text-navy text-xl leading-snug">{c.title}</h4>
                      </div>
                      <p className="text-navy/75 font-display text-sm md:text-base leading-relaxed">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* filler */}
        <div ref={ref4} className="reveal mt-16 text-center">
          <div className="quote text-navy/60 text-xl md:text-2xl">
            &mdash; The current chapter of a lifetime of service. &mdash;
          </div>
        </div>
      </div>
    </section>
  );
}
